import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const categoryTitles: Record<string, string> = {
    'short-video': t('categoryShortVideo'),
    'brand-marketing': t('categoryBrandMarketing'),
    'ad-planning': t('categoryAdPlanning'),
    'social-media': t('categorySocialMedia'),
    'content-creative': t('categoryContentCreative'),
    'full-service': t('categoryFullService'),
  };

  const categoryArticles = useMemo(() => {
    return articles.filter((a) => a.category === currentCategory.slug);
  }, [currentCategory.slug]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: t('breadcrumbHome'), item: 'https://www.guodongmedia.com' },
          { '@type': 'ListItem', position: 2, name: categoryTitles[currentCategory.slug] || currentCategory.name, item: `https://www.guodongmedia.com/category/${currentCategory.slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${categoryTitles[currentCategory.slug] || currentCategory.name} - 果冻传媒`,
        description: currentCategory.description,
        url: `https://www.guodongmedia.com/category/${currentCategory.slug}`,
        isPartOf: { '@type': 'WebSite', name: '果冻传媒' },
      },
    ],
  };

  const catName = categoryTitles[currentCategory.slug] || currentCategory.name;

  return (
    <>
      <SEOHead
        title={`${catName} - ${t('siteName')}`}
        description={currentCategory.description}
        keywords={`果冻传媒${catName},${catName}服务,果冻传媒${catName}案例`}
        canonicalUrl={`https://www.guodongmedia.com/category/${currentCategory.slug}`}
        schema={categorySchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <Breadcrumb
            items={[{ label: t('navCategory') }, { label: catName }]}
          />
          <div className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg mx-auto mb-5 ${
                  currentCategory.color === 'primary' ? 'bg-primary-100 text-primary-600' :
                  currentCategory.color === 'accent' ? 'bg-accent-100 text-accent-600' :
                  'bg-secondary-100 text-secondary-600'
                }`}>
                  <i className={`${currentCategory.icon} text-2xl`}></i>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">{catName}</h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">{currentCategory.description}</p>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-2 mb-12">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      cat.slug === currentCategory.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    {categoryTitles[cat.slug] || cat.name}
                  </Link>
                ))}
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="h-48 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <span className="text-xs text-foreground-500">{article.date}</span>
                          <span className="text-xs text-foreground-500">{article.readTime}</span>
                        </div>
                        <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 mx-auto mb-4">
                    <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">{t('noArticles')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}